import addFormats from 'ajv-formats';
import Ajv from 'ajv/dist/2020';

import Lead, { AcademicAwards } from '../types/leadDelivery';

import coerceYesNoBoolean from './coerceYesNoBoolean';
import getLdsStartDate from './getLdsStartDate';

export enum LevelOfEducation {
  HIGHSCHOOL = 'highschool',
  GED = 'ged',
  ASSOCIATES = 'associates',
  BACHELORS = 'bachelors',
  MASTERS = 'masters',
  DOCTORATE = 'doctorate',
}

export type BuildLeadObjectInputsValue<k, v = string> = {
  key: k;
  value: v;
};

export type BuildLeadObjectInputs = {
  [K in keyof Lead]: BuildLeadObjectInputsValue<K, Lead[K]>;
} & {
  graduationYear: BuildLeadObjectInputsValue<'graduationYear', string>;
  educationLevel: BuildLeadObjectInputsValue<
    'educationLevel',
    LevelOfEducation
  >;
  phone: BuildLeadObjectInputsValue<'phone', Lead['phoneNumber']>;
  address: BuildLeadObjectInputsValue<'address', string>;
  address2: BuildLeadObjectInputsValue<'address2', string>;
  city: BuildLeadObjectInputsValue<'city', string>;
  state: BuildLeadObjectInputsValue<'state', string>;
  stateAbbr: BuildLeadObjectInputsValue<'stateAbbr', string>;
  zip: BuildLeadObjectInputsValue<'ZipCode', string>;
  postalCode: BuildLeadObjectInputsValue<'postalCode', string>;
  country: BuildLeadObjectInputsValue<'country', string>;
  ipInferredPostalCode: BuildLeadObjectInputsValue<
    'ipInferredPostalCode',
    string
  >;
  startTime: BuildLeadObjectInputsValue<'startTime', Lead['startDate']>;
  militaryAffiliation: BuildLeadObjectInputsValue<
    'militaryAffiliation',
    Lead['militaryStatus']
  >;
  hasWorkExperience: BuildLeadObjectInputsValue<'hasWorkExperience', boolean>;
  hasProgramRelatedExperience: BuildLeadObjectInputsValue<
    'hasProgramRelatedExperience',
    boolean
  >;
  currentPermanentResidenceCountry: BuildLeadObjectInputsValue<
    'currentPermanentResidenceCountry',
    Lead['currentPermanentResidenceCountry']
  >;
  enrollmentYear: BuildLeadObjectInputsValue<'enrollmentYear', string>;
  sourceUrl?: BuildLeadObjectInputsValue<'sourceUrl', string>;
} & {
  [k in 'currentAcademicAward' | 'inProgressAcademicAward']: {
    value: Record<string, string>;
  };
};

type BuildLeadObject = (
  inputs: BuildLeadObjectInputs,
  schema?: any,
  leadSource?: Lead['leadSource'],
) => Lead;

// Builds a valid LDS v3 lead object to send as part of the payload to the /v3/lead endpoint
const buildLeadObject: BuildLeadObject = (
  inputs,
  schema,
  leadSource = null,
) => {
  let currentAcademicAward: string | null = null;
  let inProgressAcademicAward: string | null = null;

  if (inputs.currentAcademicAward?.value) {
    const academicAwardValue = inputs.currentAcademicAward?.value;
    // Handle when value is an object (e.g., {BACHELORS: "COMPLETED"})
    if (
      typeof academicAwardValue === 'object' &&
      !Array.isArray(academicAwardValue)
    ) {
      const key = Object.keys(academicAwardValue)[0];
      const status = academicAwardValue[key];
      if (status === 'IN_PROGRESS') {
        inProgressAcademicAward = key;
        currentAcademicAward = null;
      } else {
        currentAcademicAward = key;
        inProgressAcademicAward = null;
      }
    } else if (typeof academicAwardValue === 'string') {
      // Handle when value is a string
      if (academicAwardValue.includes('-in-progress')) {
        inProgressAcademicAward = academicAwardValue.replace(
          '-in-progress',
          '',
        );
        currentAcademicAward = null;
      } else {
        inProgressAcademicAward = null;
        currentAcademicAward = academicAwardValue;
      }
    }
  } else {
    currentAcademicAward = null;
    inProgressAcademicAward = null;
  }

  const lead: Lead = {
    firstName: inputs.firstName?.value,
    lastName: inputs.lastName?.value,
    email: inputs.email?.value,
    phoneNumber: inputs.phoneNumber?.value,
    location: {
      address: inputs.address?.value,
      address2: inputs.address2?.value,
      city: inputs.city?.value,
      // Postal code can arrive under the `zip` input key (main flow) or the
      // `postalCode` key (PRQ default questions use the LDS field name as the
      // input key). Resolve from either so the nested location field is set.
      postalCode: inputs.zip?.value ?? inputs.postalCode?.value,
      country: inputs.country?.value,
      // State abbreviation resolved from user-entered zip via validation
      state: inputs.state?.value,
      ipInferredPostalCode: inputs.ipInferredPostalCode?.value,
    },
    hsGradYear: inputs?.hsGradYear?.value
      ? Number(inputs?.hsGradYear?.value)
      : undefined,
    levelOfEducation: inputs?.levelOfEducation?.value,
    militaryStatus: coerceYesNoBoolean(inputs?.militaryStatus?.value),
    startDate:
      inputs?.startDate?.value === 'null'
        ? null
        : getLdsStartDate(inputs?.startDate?.value as unknown as number),
    gpa: inputs.gpa?.value ? Number(inputs.gpa?.value) : undefined,
    /** @TODO Investigate why we are returning booleans here */
    tcpaText: String(inputs.tcpaText?.value),
    transferCredits: inputs.transferCredits?.value,
    yearsOfWorkExperience:
      inputs.hasWorkExperience?.value === false
        ? 0
        : inputs.yearsOfWorkExperience?.value,
    yearsOfProgramRelatedExperience:
      inputs.hasProgramRelatedExperience?.value === false
        ? 0
        : inputs.yearsOfProgramRelatedExperience?.value,
    currentAcademicAward:
      currentAcademicAward as unknown as AcademicAwards | null,
    inProgressAcademicAward:
      inProgressAcademicAward as unknown as AcademicAwards | null,
    currentLicense: ['NO_LICENSE', 'IN_PROGRESS', null, undefined].includes(
      inputs.currentLicense?.value,
    )
      ? null
      : inputs.currentLicense?.value,
    inProgressLicense: inputs.inProgressLicense?.value,
    workExperienceField: inputs?.workExperienceField?.value,
    // This is fixed because a lead can only be submitted if the user consented to the program specific TCPA
    hasGivenTCPAConsent: true,
    hasGivenThirdPartySharingConsent:
      inputs?.hasGivenThirdPartySharingConsent?.value ?? false,
    hasGivenProspectSharingConsent:
      inputs?.hasGivenProspectSharingConsent?.value ?? false,
    undergraduateMajor: inputs?.undergraduateMajor?.value,
    inProgressUndergraduateMajor: inputs?.inProgressUndergraduateMajor?.value,
    learningPace: inputs?.learningPace?.value,
    programTrack: inputs?.programTrack?.value,
    currentCitizenship: inputs?.currentCitizenship?.value ?? null,
    currentPermanentResidenceCountry:
      inputs?.currentPermanentResidenceCountry?.value,
    isInARelatedRole: coerceYesNoBoolean(inputs?.isInARelatedRole?.value),
    leadSource,
    tcpaEABConsentGiven: Boolean(inputs?.tcpaEABConsentGiven?.value),
    tcpaEMCCConsentGiven: Boolean(inputs?.tcpaEMCCConsentGiven?.value),
    someCollege: Boolean(inputs?.someCollege?.value),
    is18YearsOrOlder: Boolean(inputs?.is18YearsOrOlder?.value),
    lastAttendanceYear: inputs?.enrollmentYear?.value
      ? Number(inputs?.enrollmentYear?.value)
      : undefined,
  };

  if (lead.yearsOfWorkExperience) {
    lead.yearsOfWorkExperience = Number(lead.yearsOfWorkExperience);
  }

  if (lead.yearsOfProgramRelatedExperience) {
    lead.yearsOfProgramRelatedExperience = Number(
      lead.yearsOfProgramRelatedExperience,
    );
  }

  if (lead.transferCredits) {
    lead.transferCredits = Number(lead.transferCredits);
  }

  if (schema) {
    // If schema is passed, we run ajv.validate to remove any unwanted properties from the lead object
    const ajv = new Ajv({ removeAdditional: true, allErrors: true });
    addFormats(ajv);
    const validate = ajv.compile(schema);
    validate(lead);
  }

  return lead;
};

export default buildLeadObject;
